import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, RotateCcw } from "lucide-react";
import { WelcomeScreen, type WelcomeData } from "@/components/sim/WelcomeScreen";
import { ScreenShell, GlassCard } from "@/components/sim/shared";
import { useAttemptStore } from "@/store/attemptStore";
import { parseSimIntro } from "@/lib/parseSimIntro";

/**
 * IntroPage — the briefing screen shown before a learner starts an attempt.
 * Uses the ported NeuArena WelcomeScreen: role headline + briefing cards
 * (parsed from simulations.intro_text) + journey map + Begin CTA. NeuSim
 * data flow preserved — loadTree() / startAttempt() / navigate to /play.
 */
export function IntroPage(): JSX.Element {
  const { simId } = useParams<{ simId: string }>();
  const navigate = useNavigate();
  const { tree, loadingTree, startingAttempt, error, loadTree, startAttempt } = useAttemptStore();

  useEffect(() => {
    if (simId !== undefined) void loadTree(simId);
  }, [simId, loadTree]);

  const handleBegin = async (): Promise<void> => {
    if (simId === undefined || startingAttempt) return;
    try {
      const attempt = await startAttempt(simId);
      navigate(`/sim/${simId}/attempt/${attempt.id}/play`);
    } catch {
      /* error surfaced via store.error */
    }
  };

  // Loading
  if (loadingTree || (tree === null && error === null)) {
    return (
      <ScreenShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/10 border-t-fuchsia-400" />
        </div>
      </ScreenShell>
    );
  }

  // Error
  if (error !== null) {
    return (
      <ScreenShell>
        <div className="mx-auto flex max-w-xl flex-col items-center justify-center px-4 py-24 text-center">
          <GlassCard className="p-10">
            <AlertCircle className="mx-auto h-10 w-10 text-[#f87272]" />
            <h2 className="mt-4 text-lg font-semibold">Couldn&rsquo;t load this simulation</h2>
            <p className="mt-1 text-sm text-white/60">{error}</p>
            <button
              type="button"
              onClick={() => simId !== undefined && void loadTree(simId)}
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
            >
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          </GlassCard>
        </div>
      </ScreenShell>
    );
  }

  if (tree === null) return <ScreenShell><div /></ScreenShell>;

  // Build WelcomeData from the sim tree + parsed intro_text.
  const sim = tree.simulation;
  const intro = parseSimIntro(sim.introText ?? "");
  const estMinutes = Math.max(5, Math.ceil(sim.nodeCount * 2.5));
  const competencies = sim.competencyName !== null ? [sim.competencyName] : [];

  // Decision pip labels — use each node's title where available, else "Decision N".
  // Slice to first 10 to match the WelcomeScreen journey-map design.
  const journey = tree.nodes
    .slice()
    .sort((a, b) => a.sequenceNo - b.sequenceNo)
    .filter((n, i, arr) => arr.findIndex((m) => m.sequenceNo === n.sequenceNo) === i)
    .slice(0, 10)
    .map((n) => n.title ?? `Decision ${n.sequenceNo}`);

  const data: WelcomeData = {
    participantName: intro.name !== "" ? intro.name : "You",
    role: intro.role !== "" ? intro.role : (sim.audience ?? ""),
    company: "",
    competencies,
    duration: `~${estMinutes} minutes`,
    decisions: sim.nodeCount,
    situation: intro.sections.find((s) => s.color === "cyan")?.body ?? "",
    challenge: intro.sections.find((s) => s.color === "amber")?.body ?? "",
    roleDescription: intro.sections.find((s) => s.color === "violet")?.body ?? "",
    journey,
  };

  return <WelcomeScreen data={data} onBegin={() => void handleBegin()} />;
}
